/**
 * M7 S25: the Blackmail stipend (spec §8.2, §8.3, §11; T17) and Voss's "we both sold it" counter-leverage (PC-12).
 *
 * The stipend's inputs are fixed at the Blackmail commit (`paths.ts fixStipend`: Tethys's **AI-only** tetherstone
 * income over the 10 faction days before it × the share). This file pays it: once per online faction day while
 * Blackmail is in force (`stipendActive`), `base / stipendIncomeDays × share × the stability factor`, where the factor
 * is 1 at or above `stipendFullAtStability`, 0 at or below `stipendZeroAtStability`, linear between, and 0 with the gate
 * closed (the Isolated Era: no exports, spec §11). Every credit paid is added to `stipend.paid`, which the ladder
 * claws back when the player leaves Blackmail. Nothing the player sells later changes it (T17): only the base fixed
 * at commit and the live stability are read.
 *
 * Pure (Career in, Career out); event-driven (the story link calls it on each online day), never per frame.
 */
import { withWallet } from '../../career/ops'
import type { Career } from '../../career/types'
import type { FinaleConfig } from '../schemas'
import { storyOf, withStory } from '../state'
import type { StoryResult, StoryStipend, StoryWorldView } from '../types'
import { finaleData, stipendActive } from './paths'

/** The live gate the stipend reads (from `StoryView`). */
export type StipendView = Pick<StoryWorldView, 'stability' | 'stage'>

/** The stability factor (0..1): how scared Voss still is, and whether anything is being exported at all. */
export function stipendFactor(view: StipendView, cfg: FinaleConfig): number {
  if (view.stage === 'closed') return 0
  const { stipendFullAtStability: full, stipendZeroAtStability: zero } = cfg.blackmail
  if (view.stability >= full) return 1
  if (view.stability <= zero) return 0
  return (view.stability - zero) / (full - zero)
}

/** One online day's payment (whole credits) for a stipend fixed at commit. */
export function stipendPayment(stipend: StoryStipend, view: StipendView, cfg: FinaleConfig): number {
  const daily = (stipend.base / cfg.blackmail.stipendIncomeDays) * stipend.share
  return Math.max(0, Math.round(daily * stipendFactor(view, cfg)))
}

/**
 * Pays one online day of stipend (the story link calls it once per online faction day, after `tickOnlineDay`).
 * Unchanged when Blackmail isn't in force (never committed, or left: the stipend stopped) or the payment is 0.
 */
export function payStipend(career: Career, view: StipendView, cfg: FinaleConfig = finaleData().finale): StoryResult<Career> {
  const finale = storyOf(career).finale
  const stipend = finale.stipend
  if (stipend === undefined || !stipendActive(finale)) return { career, commands: [], effects: [] }
  const amount = stipendPayment(stipend, view, cfg)
  if (amount <= 0) return { career, commands: [], effects: [] }
  const paid = withWallet(career, amount)
  const next = withStory(paid, (s) => ({
    ...s,
    finale: { ...s.finale, stipend: { ...stipend, paid: stipend.paid + amount } },
  }))
  return { career: next, commands: [], effects: [] }
}

// ---------------------------------------------------------------------------------------------
// "We both sold it" (spec §8.2 corporation variants, PC-12)
// ---------------------------------------------------------------------------------------------

/** Voss's counter-leverage lines (`comms/finale.json`). */
export const VOSS_NODES = {
  /** The player sold tetherstone personally. */
  bothSold: 'finale.voss.both_sold',
  /** The player's corporation sold some too (PC-12: "the story should notice"). */
  bothSoldCorp: 'finale.voss.both_sold_corp',
  /** No sales on record: Voss has only the lease. */
  clean: 'finale.voss.clean',
  /** After the player left Blackmail: the lawsuit, not the leverage. */
  lawsuit: 'finale.voss.lawsuit',
} as const

export interface VossLeverage {
  /** Credits from the player's own tetherstone sales (`career.tetherTotals`). */
  personalCredits: number
  /** The corporation's share of tetherstone income the link reports (0 without a corporation). */
  corpCredits: number
  nodeId: string
}

/**
 * Voss's "we both sold it" (spec §8.2): the player's own sales plus their corporation's share. The stipend is her
 * evidence against the player, never the player's proof (§2.5); this only picks what she says.
 */
export function vossLeverage(career: Career, corpCredits: number): VossLeverage {
  const personalCredits = career.tetherTotals?.credits ?? 0
  const corp = Math.max(0, corpCredits)
  const finale = storyOf(career).finale
  const left = finale.from === 'blackmail'
  const nodeId = left
    ? VOSS_NODES.lawsuit
    : corp > 0
      ? VOSS_NODES.bothSoldCorp
      : personalCredits > 0
        ? VOSS_NODES.bothSold
        : VOSS_NODES.clean
  return { personalCredits, corpCredits: corp, nodeId }
}
